import clsx from 'clsx';
import {
  type ElementType,
  forwardRef,
  type ReactElement,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import useResizeObserver from 'use-resize-observer';

import { ChevronDownSmallIcon } from '@dynatrace/strato-icons';

import { AccordionActions } from './accordion-state.js';
import {
  accordionTriggerIconCSS,
  accordionTriggerIconEndCSS,
  accordionTriggerIconExpandedCSS,
  accordionTriggerIconStartCSS,
} from './Accordion.sty.js';
import { AccordionSectionContent } from './AccordionSectionContent.js';
import { AccordionSectionLabel } from './AccordionSectionLabel.js';
import { useAccordionContext } from './contexts/AccordionContext.js';
import { AccordionSectionContext } from './contexts/AccordionSectionContext.js';
import { Button } from '../../buttons/button/Button.js';
import { useId } from '../../core/hooks/useId.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import { DataTestId } from '../../core/types/data-props.js';
import { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { fieldTokens as fieldColorsCSS } from '../../styles/field.sty.js';

// #region Typings
// Merge own props with others inherited from the underlying element type
/** @public */
export type AccordionSectionProps<E extends ElementType> = Omit<
  PolymorphicComponentProps<E, AccordionSectionOwnProps>,
  'id'
> &
  Pick<AccordionSectionOwnProps, 'id'>;

/** @public */
export interface AccordionSectionOwnProps
  extends WithChildren,
    StylingProps,
    DataTestId {
  /**
   * Determines whether the section is disabled.
   * @defaultValue false
   */
  disabled?: boolean;

  /**
   * Unique identifier of a section within an accordion.
   * This type will be reduced to string in version 2.0.0 and its value will be reflected in the
   * dom.
   */
  id: string | number;

  /**
   * Unique identifier of a section within an accordion. This will be used as an override and will function
   * as the sole identifier in version 2.0.0.
   */
  sectionId?: string | number;

  /**
   * Defines if the sections content will be kept in the DOM, even if the content
   * is not shown to the user.
   * @defaultValue false
   */
  keepMounted?: boolean;

  /**
   * The color of the accordion section.
   * Setting the color affects the button, label and content.
   */
  color?: 'neutral' | 'primary' | 'success' | 'warning' | 'critical';
}

//#endregion Typings

/**
 * Accordion Section component
 * @public
 */
export const AccordionSection: <E extends ElementType = 'div'>(
  props: AccordionSectionProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: AccordionSectionProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      disabled = false,
      id: idProp,
      sectionId,
      children,
      className: consumerClassName,
      style: consumerStyle,
      keepMounted = false,
      'data-testid': dataTestId,
      as,
      ...remainingProps
    }: AccordionSectionProps<E> = props;

    const [slots] = useSlots(children, {
      label: AccordionSectionLabel,
      content: AccordionSectionContent,
    });
    const { triggerPosition, interactive, color, size, state, dispatch } =
      useAccordionContext();

    const sectionColor = props.color ?? color;

    const triggerId = useId();
    const contentId = useId();
    const triggerIsStart = triggerPosition === 'start';
    const id = sectionId ?? idProp;

    const isExpanded = state.expanded.has(id);

    const labelId = useId();

    const containerRef = useRef<HTMLDivElement>(null);
    const { width } = useResizeObserver({ ref: containerRef.current });
    const [offsetWidth, setOffsetWidth] = useState(0);

    const updateOverflowWidth = useCallback(() => {
      if (containerRef.current) {
        setOffsetWidth(
          containerRef.current.scrollWidth - containerRef.current.offsetWidth,
        );
      }
    }, []);

    const dataDtProperties = `state:${isExpanded ? 'collapse' : 'expand'}`;

    useLayoutEffect(() => {
      updateOverflowWidth();
    }, [updateOverflowWidth, width]);

    const trigger = (
      <Button
        id={triggerId}
        onClick={() => {
          if (!interactive) {
            dispatch({ type: AccordionActions.TOGGLE_SECTION, payload: id });
          }
        }}
        color={sectionColor}
        size={size}
        disabled={disabled}
        aria-labelledby={labelId}
        aria-expanded={isExpanded}
        aria-controls={isExpanded || keepMounted ? contentId : undefined}
        data-dt-name="ExpandCollapse"
        data-dt-properties={dataDtProperties}
      >
        <Button.Prefix>
          <ChevronDownSmallIcon
            className={clsx(accordionTriggerIconCSS, {
              [accordionTriggerIconStartCSS]: triggerIsStart,
              [accordionTriggerIconEndCSS]: !triggerIsStart,
              [accordionTriggerIconExpandedCSS]: isExpanded,
            })}
          />
        </Button.Prefix>
      </Button>
    );

    const Section: ElementType = as ?? 'div';

    return (
      <Section
        data-testid={dataTestId}
        ref={forwardedRef}
        className={clsx(
          fieldColorsCSS({ color: sectionColor }),
          consumerClassName,
        )}
        style={consumerStyle}
        {...remainingProps}
      >
        {(slots.label || slots.content) && (
          <AccordionSectionContext.Provider
            value={{
              labelId,
              contentId,
              isExpanded,
              keepMounted,
              size,
            }}
          >
            <Flex
              ref={containerRef}
              flexDirection="row"
              alignItems="center"
              justifyContent={triggerIsStart ? undefined : 'space-between'}
              gap={size === 'default' ? 8 : 4}
              id={labelId}
              onClick={() => {
                if (interactive && !disabled) {
                  dispatch({
                    type: AccordionActions.TOGGLE_SECTION,
                    payload: id,
                  });
                }
              }}
              style={{
                cursor: interactive && !disabled ? 'pointer' : undefined,
                width: `calc(100% - ${offsetWidth}px)`,
              }}
              data-dt-name="ExpandCollapseHeader"
              data-dt-properties={dataDtProperties}
            >
              {triggerIsStart && trigger}
              {slots.label}
              {!triggerIsStart && trigger}
            </Flex>
            {slots.content}
          </AccordionSectionContext.Provider>
        )}
      </Section>
    );
  },
);

(
  AccordionSection as typeof AccordionSection & { displayName: string }
).displayName = 'Accordion.Section';
